import type {Task} from '@/services/graphqlHooks';

const STORAGE_KEY_PREFIX = 'viewedAnnouncementIds';

/**
 * Sanitizes a user identifier by replacing spaces and special characters
 * to make it safe for use as a localStorage key
 */
const sanitizeUserIdentifier = (identifier: string): string => {
  return identifier.replace(/\s+/g, '_').toLowerCase();
};

/**
 * Generates a user-specific storage key for viewed announcement IDs
 */
const getStorageKey = (userFullName: string): string => {
  const sanitizedName = sanitizeUserIdentifier(userFullName);
  return `${STORAGE_KEY_PREFIX}_${sanitizedName}`;
};

/**
 * Retrieves the list of viewed announcement IDs from localStorage for a specific user
 */
const getViewedIds = (userFullName: string): string[] => {
  try {
    const storageKey = getStorageKey(userFullName);
    const viewedIdsJson = localStorage.getItem(storageKey);
    if (!viewedIdsJson) {
      return [];
    }
    const parsed: unknown = JSON.parse(viewedIdsJson);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch (error) {
    console.error(
      'Error reading viewed announcement IDs from localStorage:',
      error
    );
    return [];
  }
};

/**
 * Checks if there are any new announcements by comparing the task IDs with the viewed announcement IDs stored in local storage.
 * Returns an object containing arrays of new and old announcements.
 * New announcements are those that have not been viewed by the user, while old announcements are those that have already been viewed.
 *
 * @param tasks - Array of announcement tasks to check
 * @param userFullName - User's full name to scope the viewed announcements to (supports multiple users on same device)
 */
export const checkAnnouncements = (tasks: Task[], userFullName: string) => {
  const viewedIds = getViewedIds(userFullName);

  const newAnnouncements = tasks.filter(task => !viewedIds.includes(task.id));
  const oldAnnouncements = tasks.filter(task => viewedIds.includes(task.id));

  return {
    newAnnouncements,
    oldAnnouncements,
  };
};

/**
 * Marks announcement tasks as viewed by storing their IDs in localStorage for a specific user
 *
 * @param taskIds - Array of task IDs to mark as viewed
 * @param userFullName - User's full name to scope the viewed announcements to
 */
export const markAnnouncementsAsViewed = (
  taskIds: string[],
  userFullName: string
) => {
  try {
    const existingIds = getViewedIds(userFullName);
    const updatedIds = [...new Set([...existingIds, ...taskIds])];
    const storageKey = getStorageKey(userFullName);
    localStorage.setItem(storageKey, JSON.stringify(updatedIds));
  } catch (error) {
    console.error(
      'Error saving viewed announcement IDs to localStorage:',
      error
    );
  }
};

/**
 * Clears viewed announcement IDs for tasks that no longer exist for a specific user
 * This helps prevent localStorage from accumulating stale task IDs
 *
 * @param currentTaskIds - Array of task IDs that currently exist
 * @param userFullName - User's full name to scope the cleanup to
 */
export const cleanupViewedAnnouncements = (
  currentTaskIds: string[],
  userFullName: string
) => {
  try {
    const viewedIds = getViewedIds(userFullName);
    const validIds = viewedIds.filter(id => currentTaskIds.includes(id));
    const storageKey = getStorageKey(userFullName);
    localStorage.setItem(storageKey, JSON.stringify(validIds));
  } catch (error) {
    console.error('Error cleaning up viewed announcement IDs:', error);
  }
};
